import { CSSProperties, useEffect, useRef, useState } from 'react'
import { Button, Input, Tooltip } from 'antd'
import { CloseCircleOutlined, HolderOutlined, InsertRowBelowOutlined, PlusOutlined } from '@ant-design/icons'
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import './ReportOutline.less'

let counter = 0
const newKey = (prefix: string) => `${prefix}${Date.now().toString(36)}${counter++}`

export const tableLabel = (key?: string) => (key ? key.replace(/^F\d+ Table \d+: /, '') : '')

const numbering = (items: any[]) => {
  let section = 0
  let slide = 0
  return items.map(item => {
    if (item.kind === 'section') {
      section += 1
      slide = 0
      return `Section ${section}`
    }
    slide += 1
    return section ? `${section}.${slide}` : String(slide)
  })
}

const blockEnd = (items: any[], start: number) => {
  let end = start + 1
  while (end < items.length && items[end].kind !== 'section') {
    end += 1
  }
  return end
}

const OutlineRow = ({ item, label, order, leaving, onTitle, onAddSection, onAddSlide, onDelete }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: item.key
  })
  const isSection = item.kind === 'section'
  const hint = [tableLabel(item.suggested) && `Data: ${tableLabel(item.suggested)}`, item.why].filter(Boolean).join(' · ')
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, '--i': order } as CSSProperties}
      className={`outline-row ${isSection ? 'is-section' : 'is-slide'}${isDragging ? ' is-dragging' : ''}${
        leaving ? ' is-leaving' : ''
      }`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="outline-row__handle"
        aria-label={`Move ${label}`}
        {...attributes}
        {...listeners}
      >
        <HolderOutlined />
      </button>
      <span className={isSection ? 'outline-row__badge' : 'outline-row__number'}>{label}</span>
      <div className="outline-row__main">
        <Input
          className="outline-row__title"
          bordered={false}
          value={item.title}
          placeholder={isSection ? 'Section name' : 'Slide topic'}
          onChange={e => onTitle(e.target.value)}
          aria-label={`${label} title`}
        />
        {!isSection && hint && <span className="outline-row__hint">{hint}</span>}
      </div>
      <div className="outline-row__actions">
        {isSection && (
          <Tooltip title="Add section">
            <Button
              type="text"
              shape="circle"
              className="outline-row__action"
              icon={<InsertRowBelowOutlined />}
              onClick={onAddSection}
              aria-label="Add section"
            />
          </Tooltip>
        )}
        <Tooltip title="Add slide">
          <Button
            type="text"
            shape="circle"
            className="outline-row__action"
            icon={<PlusOutlined />}
            onClick={onAddSlide}
            aria-label="Add slide"
          />
        </Tooltip>
        <Tooltip title="Delete">
          <Button
            type="text"
            shape="circle"
            className="outline-row__action is-danger"
            icon={<CloseCircleOutlined />}
            onClick={onDelete}
            aria-label="Delete"
          />
        </Tooltip>
      </div>
    </div>
  )
}

export const ReportOutline = ({ items, onChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )
  const [leaving, setLeaving] = useState<string[]>([])
  const itemsRef = useRef(items)
  itemsRef.current = items
  const firstPaint = useRef(true)
  useEffect(() => {
    firstPaint.current = false
  }, [])
  const labels = numbering(items)
  const slides = items.filter(i => i.kind === 'slide').length
  const sections = items.length - slides

  const insert = (at: number, added: any[]) => onChange([...items.slice(0, at), ...added, ...items.slice(at)])
  const section = () => ({ key: newKey('s'), kind: 'section', title: 'New section' })
  const slide = () => ({ key: newKey('t'), kind: 'slide', title: 'New slide' })

  const remove = (key: string) => {
    setLeaving(keys => [...keys, key])
    setTimeout(() => {
      onChange(itemsRef.current.filter(x => x.key !== key))
      setLeaving(keys => keys.filter(k => k !== key))
    }, 180)
  }

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return
    }
    const from = items.findIndex(i => i.key === active.id)
    const end = items[from].kind === 'section' ? blockEnd(items, from) : from + 1
    const moved = items.slice(from, end)
    if (moved.some(i => i.key === over.id)) {
      return
    }
    const rest = [...items.slice(0, from), ...items.slice(end)]
    const target = rest.findIndex(i => i.key === over.id)
    const at = items.findIndex(i => i.key === over.id) > from ? target + 1 : target
    onChange([...rest.slice(0, at), ...moved, ...rest.slice(at)])
  }

  return (
    <div className="outline">
      <div className="outline__summary">
        <span>
          <strong>{sections}</strong> sections · <strong>{slides}</strong> slides
        </span>
        <span className="outline__hint">
          Drag the handle to reorder. Dragging a section moves its slides with it. Click a name to change it.
        </span>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items.map(i => i.key)} strategy={verticalListSortingStrategy}>
          <div className="outline__list">
            {items.map((item, i) => (
              <OutlineRow
                key={item.key}
                item={item}
                label={labels[i]}
                order={firstPaint.current ? Math.min(i, 14) : 0}
                leaving={leaving.includes(item.key)}
                onTitle={(title: string) => onChange(items.map(x => (x.key === item.key ? { ...x, title } : x)))}
                onAddSection={() => insert(blockEnd(items, i), [section(), slide()])}
                onAddSlide={() => insert(item.kind === 'section' ? blockEnd(items, i) : i + 1, [slide()])}
                onDelete={() => remove(item.key)}
              />
            ))}
            {!items.length && <div className="outline__empty">The outline is empty. Add a section to start.</div>}
          </div>
        </SortableContext>
      </DndContext>
      <Button
        type="dashed"
        block
        size="large"
        className="outline__add"
        icon={<InsertRowBelowOutlined />}
        onClick={() => onChange([...items, section(), slide()])}
      >
        Add section
      </Button>
    </div>
  )
}
